import { Collapse, Flex, Tag } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import styled from 'styled-components';
import { BluePrimary } from '../../../../../styles';

const ToolHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #595959;
`;

const WorkingText = styled.span`
  color: #8c8c8c;
  font-size: 12px;
  font-style: italic;
`;

/**
 * ToolWrapper - Wraps tool renderers in a collapsible container
 *
 * Features:
 * - Displays tool name and status in header
 * - Auto-expands when tool is pending/running
 * - Auto-collapses when tool completes (but user can manually expand)
 * - Shows loading indicator for pending tools
 *
 * @param {Object} props
 * @param {string} props.toolName - Display name of the tool
 * @param {boolean} props.isPending - Whether the tool is still executing
 * @param {React.ReactNode} props.children - Tool content to display
 * @param {React.ReactNode} props.icon - Optional icon to display in header
 */
function ToolWrapper({ toolName, isPending, children, icon = null }) {
  // Manage collapse state - auto-expand when pending, allow manual toggle
  const [activeKey, setActiveKey] = useState(isPending ? ['1'] : []);

  // Auto-expand when tool becomes pending
  useEffect(() => {
    if (isPending) {
      setActiveKey(['1']);
    } else {
      // Auto-collapse when tool completes (only if it was previously pending)
      setActiveKey([]);
    }
  }, [isPending]);

  const header = (
    <ToolHeader>
      {icon}
      <span>{toolName}</span>
      {isPending && (
        <Flex align="center" gap={6}>
          <BeatLoader color={BluePrimary} size={4} />
          <WorkingText>Working...</WorkingText>
        </Flex>
      )}
      {!isPending && (
        <Tag color="success" style={{ fontSize: 11 }}>
          Completed
        </Tag>
      )}
    </ToolHeader>
  );

  return (
    <Collapse
      activeKey={activeKey}
      onChange={setActiveKey}
      ghost
      size="small"
      style={{ marginBottom: 8 }}
      items={[
        {
          key: '1',
          label: header,
          children: <div style={{ paddingTop: 8 }}>{children}</div>,
        },
      ]}
    />
  );
}

ToolWrapper.propTypes = {
  toolName: PropTypes.string.isRequired,
  isPending: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  icon: PropTypes.node,
};

export default ToolWrapper;
